import { defineStore } from 'pinia'
import { ApiResponseError } from '~/api/client'
import {
  createImpersonationSession,
  deleteImpersonationSession,
  getMeContext,
  type EffectiveUserContext,
} from '~/api/impersonation'
import type { CustomerPlan, CustomerTeamWorkflowSettings } from '~/api/mappers'

const isExpiredImpersonationSession = (expiresAt: string | null, now = Date.now()) => {
  if (!expiresAt) return true

  const expiresAtTime = Date.parse(expiresAt)
  return Number.isNaN(expiresAtTime) || expiresAtTime <= now
}

export const useImpersonateStore = defineStore('impersonate', {
  state: () => ({
    impersonationSessionId: null as string | null,
    impersonationExpiresAt: null as string | null,
    effectiveCustomerId: null as string | null,
    effectiveCustomerName: null as string | null,
    impersonatedCustomerName: null as string | null,
    impersonatedCustomerPlan: null as CustomerPlan | null,
    impersonatedTeamWorkflowSettings: null as CustomerTeamWorkflowSettings | null,
  }),

  getters: {
    isImpersonating: state =>
      !!state.impersonationSessionId
      && !isExpiredImpersonationSession(state.impersonationExpiresAt),
    impersonatedCustomerId: state => state.effectiveCustomerId,
  },

  actions: {
    applyContext(context: EffectiveUserContext) {
      this.effectiveCustomerId = context.effective_customer_id
      this.effectiveCustomerName = context.effective_customer_name ?? null

      if (!context.is_impersonating || !context.impersonation_session?.active) {
        this.stopLocalImpersonation()
        this.effectiveCustomerId = context.effective_customer_id
        this.effectiveCustomerName = context.effective_customer_name ?? null
        return
      }

      this.impersonationSessionId = context.impersonation_session.id
      this.impersonationExpiresAt = context.impersonation_session.expires_at
      this.impersonatedCustomerName
        = context.impersonation_session.target_customer_name
          ?? context.effective_customer_name
          ?? null
    },

    clearExpiredImpersonation(now = Date.now()) {
      if (
        !this.impersonationSessionId
        || !isExpiredImpersonationSession(this.impersonationExpiresAt, now)
      ) {
        return false
      }

      this.stopLocalImpersonation()
      $log('Cleared expired impersonation session')
      return true
    },

    async fetchContext() {
      this.clearExpiredImpersonation()

      try {
        const context = await getMeContext()
        this.applyContext(context)
        return context
      }
      catch (error) {
        $logError('Error fetching user context:', error)
        this.stopLocalImpersonation()
        return null
      }
    },

    async startImpersonating(customerId: string, customerName: string) {
      const session = await createImpersonationSession(customerId)

      this.impersonationSessionId = session.id
      this.impersonationExpiresAt = session.expires_at
      this.effectiveCustomerId = session.target_customer_id
      this.effectiveCustomerName = session.target_customer_name ?? customerName
      this.impersonatedCustomerName = session.target_customer_name ?? customerName
      this.impersonatedCustomerPlan = null
      this.impersonatedTeamWorkflowSettings = null
      $log(`Impersonating customer: ${customerName} (${customerId})`)
    },

    setImpersonatedCustomerPlan(plan: CustomerPlan | null) {
      this.impersonatedCustomerPlan = plan
    },

    setImpersonatedTeamWorkflowSettings(settings: CustomerTeamWorkflowSettings | null) {
      this.impersonatedTeamWorkflowSettings = settings
    },

    stopLocalImpersonation() {
      this.impersonationSessionId = null
      this.impersonationExpiresAt = null
      this.effectiveCustomerId = null
      this.effectiveCustomerName = null
      this.impersonatedCustomerName = null
      this.impersonatedCustomerPlan = null
      this.impersonatedTeamWorkflowSettings = null
    },

    async stopImpersonating() {
      const sessionId = this.impersonationSessionId
      if (sessionId) {
        try {
          await deleteImpersonationSession(sessionId)
        }
        catch (error) {
          if (
            error instanceof ApiResponseError
            && [403, 404].includes(error.response.status)
          ) {
            this.stopLocalImpersonation()
            $log('Stopped impersonating customer')
            return
          }
          $logError('Error stopping impersonation:', error)
        }
      }

      this.stopLocalImpersonation()
      $log('Stopped impersonating customer')
    },
  },
  persist: {
    pick: ['impersonationSessionId', 'impersonationExpiresAt', 'impersonatedCustomerName'],
  },
})
